import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import api, { apiErr, money, fmtDate } from "@/lib/api";
import { PageHero, Field } from "@/components/site/ui";
import StatusBadge from "@/components/StatusBadge";

const inputCls = "w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-sm text-foreground focus:border-gold focus:outline-none";

export default function ManageReservation() {
  const [params] = useSearchParams();
  const [conf, setConf] = useState(params.get("c") || "");
  const [email, setEmail] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!conf || !email) { toast.error("Enter your confirmation number and email."); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/reservations/lookup", { params: { confirmation_number: conf, email } });
      setRes(data);
    } catch (err) { toast.error(apiErr(err)); setRes(null); } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHero eyebrow="Your Reservation" title="Manage Reservation" subtitle="Look up your reservation with your confirmation number." compact />
      <div className="max-w-2xl mx-auto px-6 py-16">
        <form onSubmit={lookup} className="glass rounded-lg p-8 grid sm:grid-cols-2 gap-4">
          <Field label="Confirmation number" required><input className={inputCls} value={conf} onChange={(e) => setConf(e.target.value)} placeholder="TD-2026-0001" data-testid="manage-conf" /></Field>
          <Field label="Email" required><input type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="manage-email" /></Field>
          <button className="btn-gold py-3 text-sm flex items-center justify-center gap-2 sm:col-span-2" data-testid="manage-lookup">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />} Find Reservation
          </button>
        </form>

        {res && (
          <div className="glass rounded-lg p-8 mt-8" data-testid="manage-result">
            <div className="flex justify-between items-start flex-wrap gap-3">
              <div>
                <p className="text-xs tracking-label uppercase text-muted-foreground">Confirmation</p>
                <p className="font-serif text-3xl text-gold">{res.confirmation_number}</p>
              </div>
              <StatusBadge status={res.status} />
            </div>
            <div className="gold-line my-5" />
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">Experience</span><p>{res.experience_title}</p></div>
              <div><span className="text-muted-foreground">Dates</span><p>{fmtDate(res.arrival_date)} → {fmtDate(res.departure_date)}</p></div>
              <div><span className="text-muted-foreground">Party</span><p>{res.adults} adults · {res.children} children</p></div>
              <div><span className="text-muted-foreground">Payment status</span><p>{res.payment_status}</p></div>
            </div>
            {res.folio && (
              <div className="mt-6 border-t border-gold/15 pt-5">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total</span><span>{money(res.folio.total)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Paid</span><span>{money(res.folio.paid)}</span></div>
                <div className="flex justify-between font-medium mt-1"><span>Balance</span><span className="text-gold">{money(res.folio.balance)}</span></div>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-6">Sign in to your guest account to complete digital check-in, request changes, and message concierge.</p>
          </div>
        )}
      </div>
    </div>
  );
}
